// Candidates > Schedule rounds — the Final Interview Loop page (`#requisitions/<R>/candidates/<aid>/schedule`; lane B5
// S12, screens-walks §2.6, DC-34): "Coordinator schedules virtual/onsite/hybrid rounds, presentations, assessments and
// executive conversations." The loop's rounds in sequence on the rail, each in its interviewer's hue, and the loop day
// with every booked round as a band; "Cannot schedule if required panel assignment is declined or coverage invalid."
// while a required seat is declined or coverage is invalid, each gap linked to Part 2 where it clears. Each round is
// scheduled on its own round page (one Schedule per round, "a later change to one round re-opens only that round"),
// where the round's packet is issued with its booking; the booked rounds stack here as Outlook events with the
// candidate itinerary to copy. An unknown or unauthorised application is the canonical not-found.
// Registered as a view (views/b5/register.ts), so it renders in MissionShell's content slot inside its one McRoot.

import { useEffect } from "react";
import type { MeetingOut, SchedulingOpenOutput } from "@/api/tools";
import { schedulingOpen } from "@/api/tools";
import { errorText, isClientRefusal, useRead } from "@/api/useTool";
import { useViewState } from "@/shell/viewState";
import { NotFound } from "@/views/NotFound";
import {
  Banner, CheckRow, Checklist, Itinerary, LOOP_LABELS, LoopSequence, MeetingEvent, PageHeader, Pill, Responses, SCHEDULING_LABELS,
  SCHEDULING_STATUS, Stack, formatDateTime, personHue, toneOf, type AvailRow, type StatusWord,
} from "@/mc";
import { wallTime, zonedInstant } from "./dialogs";
import { READINESS_LABELS } from "./labels";
import { itinerary, responses } from "./RequestSlideOver";
import { formatWord, hsId, statusWord } from "./workbench";

const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17] as const;

export function LoopSchedule({ rid, aid }: { rid: string; aid: string }) {
  const { register } = useViewState();
  const read = useRead(["scheduling.open", { application_id: aid, meeting_type: "final_loop" }], () => schedulingOpen({ application_id: aid, meeting_type: "final_loop" }));
  useEffect(() => register({ view: "loop-schedule", ids: { requisition: rid, application: aid }, summary: read.data?.status ?? "" }), [register, rid, aid, read.data?.status]);
  const route = `requisitions/${rid}/candidates/${aid}/schedule`;
  if (read.error) return isClientRefusal(read.error) ? <NotFound route={route} /> : <Banner tone="red" live>{errorText(read.error)}</Banner>;
  const request = read.data;
  if (request && request.requisition_id !== rid) return <NotFound route={route} />;
  return (
    <Stack gap={16}>
      <PageHeader
        title={request ? [LOOP_LABELS.finalInterviewLoop, request.candidate_display_name].filter(Boolean).join(" · ") : LOOP_LABELS.finalInterviewLoop}
        pills={request ? <Pill tone={toneOf(statusWord(request.status) as StatusWord)}>{statusWord(request.status)}</Pill> : undefined}
        meta={request ? [request.requisition_id, request.requisition_title] : undefined}
        loading={!request}
      />
      {request ? <Loop request={request} rid={rid} aid={aid} /> : null}
    </Stack>
  );
}

function Loop({ request, rid, aid }: { request: SchedulingOpenOutput; rid: string; aid: string }) {
  const tz = request.event_tz;
  const loop = request.loop;
  const live = request.meetings.filter((m) => m.state !== "cancelled");
  const meetingOf = (rd: string): MeetingOut | undefined => live.find((m) => m.round_def_id === rd);
  const rounds = loop?.rounds ?? [];
  const part2 = `#requisitions/${rid}/intake/part2`;
  const firstBooked = live.find((m) => m.starts_at);
  const day = firstBooked?.starts_at ? wallTime(firstBooked.starts_at, tz).slice(0, 10) : null;
  const cells = day
    ? HOURS.map((h) => {
      const start = zonedInstant(`${day}T${String(h).padStart(2, "0")}:00`, tz) ?? "";
      return { start: Date.parse(start), end: Date.parse(start) + 3_600_000 };
    })
    : [];
  const interviewer = (m: MeetingOut | undefined) => m?.attendees.find((a) => a.role === "interviewer");
  const rows: AvailRow[] = rounds.map((rd) => ({
    name: interviewer(meetingOf(rd.round_def_id))?.name ?? rd.label,
    zone: formatWord(rd.format) ?? undefined,
    cells: cells.map(() => "free" as const),
  }));
  const bands = rounds.flatMap((rd, row) => {
    const m = meetingOf(rd.round_def_id);
    if (!m?.starts_at || !m.ends_at || !cells.length) return [];
    const s = Date.parse(m.starts_at);
    const e = Date.parse(m.ends_at);
    const hit = cells.map((c, i) => (c.start < e && s < c.end ? i : -1)).filter((i) => i >= 0);
    return hit.length ? [{ row, from: hit[0]!, to: hit[hit.length - 1]!, round: rd.round_def_id }] : [];
  });
  const blocked = loop && !loop.ready;
  return (
    <Stack gap={16}>
      {blocked ? (
        <Banner tone="warn">
          <b>{SCHEDULING_LABELS.panelDeclined}</b>
          <Checklist>
            {rounds.filter((rd) => rd.blocked).map((rd) => (
              <CheckRow key={rd.round_def_id} status="bad" href={part2} hs={`sched.loop.blocked.${hsId(rd.round_def_id)}`}>{rd.label}</CheckRow>
            ))}
            {loop.coverage_gaps.map((code) => (
              <CheckRow key={code} status="bad" href={part2} hs={`sched.loop.gap.${hsId(code)}`}>{`${READINESS_LABELS.coverage}: ${code}`}</CheckRow>
            ))}
          </Checklist>
        </Banner>
      ) : null}
      <LoopSequence
        rounds={rounds.map((rd) => {
          const m = meetingOf(rd.round_def_id);
          const who = interviewer(m);
          return {
            key: rd.round_def_id,
            title: rd.label,
            status: m ? SCHEDULING_STATUS.scheduled : rd.blocked ? SCHEDULING_LABELS.conflict : SCHEDULING_STATUS.requested,
            state: m ? ("done" as const) : ("todo" as const),
            href: `#requisitions/${rid}/candidates/${aid}/rounds/${rd.round_def_id}`,
            hue: who ? personHue(who.person_id) : undefined,
            hs: `sched.loop.round.${hsId(rd.round_def_id)}`,
          };
        })}
        columns={cells.map((c) => wallTime(new Date(c.start).toISOString(), tz).slice(11, 16))}
        rows={rows}
        bands={bands}
        tasks={live.some((m) => m.room_slug || m.teams_url) ? [{
          key: "room",
          kind: "room",
          detail: live.map((m) => m.room_slug).filter(Boolean).join(", ") || undefined,
          tasks: [
            { task: "room", done: live.some((m) => m.room_slug) },
            { task: "link", done: live.some((m) => m.teams_url) },
          ],
        }] : []}
        
      />
      {live.map((m) => (
        <MeetingEvent
          key={m.meeting_event_id}
          title={m.round_label ?? LOOP_LABELS.finalInterviewLoop}
          items={itinerary(m)}
          teams={m.teams_url ? { url: m.teams_url, hs: `sched.loop.teams.${hsId(m.meeting_event_id)}` } : undefined}
          meetingId={m.teams_meeting_id ?? m.provider_event_id ?? undefined}
        >
          <Responses items={responses(m)} mode="view" />
        </MeetingEvent>
      ))}
      {live.length ? (
        <Itinerary
          items={live.filter((m) => m.starts_at).map((m) => [m.round_label ?? LOOP_LABELS.finalInterviewLoop, `${formatDateTime(m.starts_at!, tz)} · ${tz}`] as const)}
          
        />
      ) : null}
    </Stack>
  );
}
